import { Box, Card, CircularProgress, Container, Grid, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import UsersCountSummary from "src/components/users/UsersCountSummary";
import UsersSignupChart from "src/components/users/UsersSignupChart";
import { AppWidgetSummary } from "src/sections/@dashboard/app";
import { get } from "src/utils/networkUtils";

const STATS = [
	{
		title: "Total IAP",
		field: "totalProfit",
		color: "primary",
		format: "currency",
	},
	{
		title: "Paying Users",
		field: "payUserPct",
		color: "primary",
		format: "percent",
	},
	{
		title: "This month",
		field: "thisMonthProfit",
		color: "secondary",
		format: "currency",
	},
	{
		title: "Last month",
		field: "lastMonthProfit",
		color: "secondary",
		format: "currency",
	},
	{
		title: "This Week",
		field: "thisWeekProfit",
		color: "warning",
		format: "currency",
	},
	{
		title: "Last Week",
		field: "lastWeekProfit",
		color: "warning",
		format: "currency",
	},
	{
		title: "Today",
		field: "todayProfit",
		color: "info",
		format: "currency",
	},
	{
		title: "Yesterday",
		field: "yesterdayProfit",
		color: "info",
		format: "currency",
	},
	{
		title: "Paying Users (1+ Units)",
		field: "totalPayUsers",
		color: "success",
		format: "shorten",
	},
	{
		title: "Avg Rev/ Paying user",
		field: "avgProfitPerPayUser",
		color: "success",
		format: "shortencurrency",
	},
	{
		title: "Avg Rev per all users",
		field: "avgProfitPerAllUsers",
		color: "success",
		format: "shortencurrency",
	},
	{
		title: "Units / Paying users",
		field: "avgPruchasePerUser",
		color: "success",
		format: "shorten",
	},
	{
		title: "One-time buyers (1 Unit)",
		field: "oneTimePayPct",
		color: "success",
		format: "percent",
	},
	{
		title: "Recurring buyers (1+ Units)",
		field: "multipleTimePayPct",
		color: "success",
		format: "percent",
	},
];

export default function HomePage() {
	const [statsLoading, setStatsLoading] = useState(true);
	const [purchasesLoading, setPurchasesLoading] = useState(true);
	const [stats, setStats] = useState(null);
	const [purchasesStats, setPurchasesStats] = useState(null);
	console.log("purchasesStats", purchasesStats);
	useEffect(() => {
		setStatsLoading(true);
		get(`/admin/users/stats`)
			.then((response) => {
				console.log(response);
				setStats(response);
				setStatsLoading(false);
			})
			.catch((error) => {
				console.error("Error loading stats:", error);
				setStatsLoading(false);
			});

		setPurchasesLoading(true);
		get(`/admin/marketplace/purchases/stats`)
			.then((response) => {
				setPurchasesStats(response.stats);
				setPurchasesLoading(false);
			})
			.catch((error) => {
				console.error("Error loading purchases stats:", error);
				setPurchasesLoading(false);
			});
	}, []);

	return (
		<>
			<Helmet>
				<title> Home </title>
			</Helmet>

			<Container maxWidth="xl">
				<Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
					<Typography variant="h4" gutterBottom>
						Home
					</Typography>
				</Stack>
				<Grid container spacing={2}>
					<Grid item xs={12} md={6} lg={6}>
						<Card>
							{statsLoading ? (
								<Box
									sx={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										minHeight: 300,
									}}
								>
									<CircularProgress />
								</Box>
							) : stats ? (
								<UsersSignupChart
									title="Signups per Day"
									subheader=""
									height={280}
									chartLabels={stats.usersByDate.map((date) => date.date)}
									chartData={[
										{
											name: "Signups",
											type: "column",
											fill: "solid",
											data: stats.usersByDate.map((date) => date.userCount),
										},
									]}
								/>
							) : (
								<Box sx={{ p: 3, textAlign: "center" }}>
									<Typography variant="body2" color="text.secondary">
										Failed to load signup data
									</Typography>
								</Box>
							)}
						</Card>
					</Grid>
					<Grid item xs={12} md={6} lg={6}>
						<Card>
							{purchasesLoading ? (
								<Box
									sx={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										minHeight: 300,
									}}
								>
									<CircularProgress />
								</Box>
							) : purchasesStats ? (
								<UsersSignupChart
									title="Revenue Per Day"
									subheader=""
									height={280}
									chartLabels={purchasesStats.profitPerDay.map((date) => date.date)}
									chartData={[
										{
											name: "Revenue ($)",
											type: "column",
											fill: "solid",
											data: purchasesStats.profitPerDay.map((date) => date.profit),
										},
									]}
								/>
							) : (
								<Box sx={{ p: 3, textAlign: "center" }}>
									<Typography variant="body2" color="text.secondary">
										Failed to load revenue data
									</Typography>
								</Box>
							)}
						</Card>
					</Grid>
				</Grid>
				<Box sx={{ mt: 3, mb: 2 }}>
					<Grid container spacing={1}>
						<Grid item xs={2}>
							<UsersCountSummary title="Total Users" total={stats?.usersCount ?? 0} />
						</Grid>
						<Grid item xs={2}>
							<UsersCountSummary title="This Month" total={stats?.newUsersThisMonthCount ?? 0} color="info" />
						</Grid>
						<Grid item xs={2}>
							<UsersCountSummary title="Last Month" total={stats?.newUsersLastMonthCount ?? 0} color="info" />
						</Grid>
						<Grid item xs={2}>
							<UsersCountSummary title="This Week" total={stats?.newUsersThisWeekCount ?? 0} color="info" />
						</Grid>
						<Grid item xs={2}>
							<UsersCountSummary title="Last Week" total={stats?.newUsersLastWeekCount ?? 0} color="info" />
						</Grid>
						<Grid item xs={2}>
							<UsersCountSummary title="Today" total={stats?.newUsersCount ?? 0} color="info" />
						</Grid>
						<Grid item xs={2}>
							<UsersCountSummary title="Yesterday" total={stats?.newUsersYesterdayCount ?? 0} color="info" />
						</Grid>
						{purchasesStats &&
							STATS.map((item, index) => (
								<Grid item xs={2} key={index}>
									<AppWidgetSummary
										title={item.title}
										total={purchasesStats[item.field]}
										icon={"eva:behance-outline"}
										color={item.color}
										format={item.format}
									/>
								</Grid>
							))}
					</Grid>
				</Box>
			</Container>
		</>
	);
}
